/**
 * Cards do Tempo de Corte removidos manualmente.
 * Impede merge/backup/sync e o botão "Criar cards faltantes" de ressuscitar o card.
 * Persistência: localStorage + IndexedDB (compartilhado entre janelas NW.js).
 */

export const CHAVE_TC_CARDS_EXCLUIDOS = 'produtos-tempo-corte-excluidos-ade';
export const CHAVE_TC_CARDS_EXCLUIDOS_EPOCH = 'produtos-tempo-corte-excluidos-epoch-ade';

const DB_NAME = 'ade-tempo-corte-db';
const DB_VERSION = 9;
const STORE = 'tempoCorte';
const KEY_EXCLUIDOS_IDB = 'produtosTempoCorteExcluidos';

/** Sobe a cada exclusão — invalida gravações antigas em paralelo (debounce/sync). */
export function lerEpochExclusoesCardsTempoCorte(): number {
  try {
    const n = Number(localStorage.getItem(CHAVE_TC_CARDS_EXCLUIDOS_EPOCH) || '0');
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function bumpEpochExclusoesCardsTempoCorte(): number {
  const next = lerEpochExclusoesCardsTempoCorte() + 1;
  try {
    localStorage.setItem(CHAVE_TC_CARDS_EXCLUIDOS_EPOCH, String(next));
  } catch {
    /* quota */
  }
  return next;
}

function normalizarChave(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

/** Extrai números de lote do texto (8670, 8670/8671, …). */
function numerosLoteTexto(s: string): string[] {
  const nums: string[] = [];
  const partes = String(s || '').split(
    /(?:\s+E\s+|\s*,\s*|\s*;\s*|\s*\/\s*|\s*\+\s*|\s*&\s*|\s*[-–—]\s*)/i
  );
  for (const p of partes) {
    const digitos = String(p || '').replace(/\D/g, '');
    if (digitos) nums.push(digitos);
  }
  const soDigitos = String(s || '').replace(/\D/g, '');
  if (soDigitos && !nums.includes(soDigitos)) nums.push(soDigitos);
  return [...new Set(nums)];
}

/** Normaliza número/espaços, mas MANTÉM MDF/AGLO no nome (card distinto). */
function chaveNomeCardComMaterial(nome: string): string {
  return normalizarChave(nome)
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
      const trimmed = decPart.replace(/0+$/, '');
      return trimmed ? `${intPart}.${trimmed}` : intPart;
    })
    .replace(/\s+/g, ' ')
    .trim();
}

/** Legado / cadastro: sem MDF/AGLO. */
function chaveNomeCardSemMaterial(nome: string): string {
  return chaveNomeCardComMaterial(nome)
    .replace(/\bMDF\b/g, '')
    .replace(/\bAGLOMERADO\b/g, '')
    .replace(/\bAGLO\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function chaveParCardTempoCorte(lote: string, nomeProduto: string): string {
  return `${normalizarChave(lote)}|${chaveNomeCardComMaterial(nomeProduto)}`;
}

function chaveParSemMaterial(lote: string, nomeProduto: string): string {
  return `${normalizarChave(lote)}|${chaveNomeCardSemMaterial(nomeProduto)}`;
}

type ExclusoesPayload = { ids: string[]; pares: string[] };

function lerExclusoesLocal(): { ids: Set<string>; pares: Set<string> } {
  try {
    const raw = localStorage.getItem(CHAVE_TC_CARDS_EXCLUIDOS);
    if (!raw) return { ids: new Set(), pares: new Set() };
    const parsed = JSON.parse(raw) as ExclusoesPayload;
    return {
      ids: new Set(
        (Array.isArray(parsed?.ids) ? parsed.ids : [])
          .map((x) => String(x || '').trim())
          .filter(Boolean)
      ),
      pares: new Set(
        (Array.isArray(parsed?.pares) ? parsed.pares : [])
          .map((x) => String(x || '').trim())
          .filter(Boolean)
      ),
    };
  } catch {
    return { ids: new Set(), pares: new Set() };
  }
}

function gravarExclusoesLocal(ids: Set<string>, pares: Set<string>): void {
  try {
    localStorage.setItem(
      CHAVE_TC_CARDS_EXCLUIDOS,
      JSON.stringify({
        ids: [...ids].slice(-5000),
        pares: [...pares].slice(-5000),
      } satisfies ExclusoesPayload)
    );
  } catch {
    /* quota */
  }
}

function abrirDbExclusoes(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function lerExclusoesIndexedDB(): Promise<{ ids: Set<string>; pares: Set<string> }> {
  try {
    const db = await abrirDbExclusoes();
    const data = await new Promise<ExclusoesPayload | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY_EXCLUIDOS_IDB);
      req.onsuccess = () => resolve((req.result as ExclusoesPayload) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!data || typeof data !== 'object') return { ids: new Set(), pares: new Set() };
    return {
      ids: new Set(
        (Array.isArray(data.ids) ? data.ids : []).map((x) => String(x || '').trim()).filter(Boolean)
      ),
      pares: new Set(
        (Array.isArray(data.pares) ? data.pares : [])
          .map((x) => String(x || '').trim())
          .filter(Boolean)
      ),
    };
  } catch {
    return { ids: new Set(), pares: new Set() };
  }
}

async function gravarExclusoesIndexedDB(ids: Set<string>, pares: Set<string>): Promise<void> {
  try {
    const db = await abrirDbExclusoes();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(
        {
          ids: [...ids].slice(-5000),
          pares: [...pares].slice(-5000),
        } satisfies ExclusoesPayload,
        KEY_EXCLUIDOS_IDB
      );
    });
    db.close();
  } catch {
    /* ignora */
  }
}

function unirExclusoes(
  a: { ids: Set<string>; pares: Set<string> },
  b: { ids: Set<string>; pares: Set<string> }
): { ids: Set<string>; pares: Set<string> } {
  return {
    ids: new Set([...a.ids, ...b.ids]),
    pares: new Set([...a.pares, ...b.pares]),
  };
}

/** Cache em memória (após hidratar IDB). */
let cacheExclusoes: { ids: Set<string>; pares: Set<string> } | null = null;

function obterExclusoesSincrono(): { ids: Set<string>; pares: Set<string> } {
  if (cacheExclusoes) {
    const ls = lerExclusoesLocal();
    cacheExclusoes = unirExclusoes(cacheExclusoes, ls);
    return cacheExclusoes;
  }
  return lerExclusoesLocal();
}

function aplicarExclusoes(ids: Set<string>, pares: Set<string>): void {
  cacheExclusoes = { ids, pares };
  gravarExclusoesLocal(ids, pares);
  bumpEpochExclusoesCardsTempoCorte();
  void gravarExclusoesIndexedDB(ids, pares);
}

/**
 * Todas as chaves lote|nome possíveis para um card.
 * Inclui lote cru, cada número do lote, nome com e sem MDF/AGLO.
 */
function paresExclusaoParaCard(lote: string, nome: string): string[] {
  const out = new Set<string>();
  const loteKeys = [String(lote || '').trim(), ...numerosLoteTexto(lote)].filter(Boolean);
  for (const lk of loteKeys) {
    const com = chaveParCardTempoCorte(lk, nome);
    if (com !== '|') out.add(com);
    const sem = chaveParSemMaterial(lk, nome);
    if (sem !== '|' && sem !== com) out.add(sem);
  }
  return [...out];
}

/**
 * Une localStorage + IndexedDB. Chamar no boot / antes de coletar cards.
 */
export async function hidratarExclusoesCardsTempoCorte(): Promise<void> {
  const ls = lerExclusoesLocal();
  const idb = await lerExclusoesIndexedDB();
  const unido = unirExclusoes(ls, idb);
  cacheExclusoes = unido;
  gravarExclusoesLocal(unido.ids, unido.pares);
  await gravarExclusoesIndexedDB(unido.ids, unido.pares);
}

export function lerExclusoesCardsTempoCorte(): {
  ids: Set<string>;
  pares: Set<string>;
} {
  return obterExclusoesSincrono();
}

export function registrarExclusaoCardTempoCorte(card: {
  id?: string;
  nome?: string;
  lote?: string;
}): void {
  const { ids, pares } = obterExclusoesSincrono();
  const id = String(card.id || '').trim();
  if (id) ids.add(id);
  const lote = String(card.lote || '').trim();
  const nome = String(card.nome || '').trim();
  for (const p of paresExclusaoParaCard(lote, nome)) pares.add(p);
  aplicarExclusoes(ids, pares);
}

export function registrarExclusoesCardsTempoCorte(
  cards: ReadonlyArray<{ id?: string; nome?: string; lote?: string }>
): void {
  if (!cards.length) return;
  const { ids, pares } = obterExclusoesSincrono();
  for (const card of cards) {
    const id = String(card.id || '').trim();
    if (id) ids.add(id);
    const lote = String(card.lote || '').trim();
    const nome = String(card.nome || '').trim();
    for (const p of paresExclusaoParaCard(lote, nome)) pares.add(p);
  }
  aplicarExclusoes(ids, pares);
}

/** Usuário salvou/gerou o card de novo — pode voltar a aparecer. */
export function liberarExclusaoCardTempoCorte(lote: string, nomeProduto: string, id?: string): void {
  const { ids, pares } = obterExclusoesSincrono();
  let mudou = false;
  const idLimpo = String(id || '').trim();
  if (idLimpo && ids.delete(idLimpo)) mudou = true;
  for (const p of paresExclusaoParaCard(lote, nomeProduto)) {
    if (pares.delete(p)) mudou = true;
  }
  if (mudou) aplicarExclusoes(ids, pares);
}

export function cardTempoCorteFoiExcluidoManual(card: {
  id?: string;
  nome?: string;
  lote?: string;
}): boolean {
  const { ids, pares } = obterExclusoesSincrono();
  const id = String(card.id || '').trim();
  if (id && ids.has(id)) return true;
  const lote = String(card.lote || '').trim();
  const nome = String(card.nome || '').trim();
  if (!lote || !nome) return false;
  for (const p of paresExclusaoParaCard(lote, nome)) {
    if (pares.has(p)) return true;
  }
  return false;
}

export function parLoteProdutoCardExcluido(lote: string, produtoNome: string): boolean {
  const { pares } = obterExclusoesSincrono();
  for (const p of paresExclusaoParaCard(lote, produtoNome)) {
    if (pares.has(p)) return true;
  }
  return false;
}

export function filtrarCardsTempoCorteNaoExcluidos<
  T extends { id?: string; nome?: string; lote?: string },
>(lista: T[]): T[] {
  const { ids, pares } = obterExclusoesSincrono();
  if (ids.size === 0 && pares.size === 0) return lista;
  return lista.filter((c) => {
    const id = String(c.id || '').trim();
    if (id && ids.has(id)) return false;
    const lote = String(c.lote || '').trim();
    const nome = String(c.nome || '').trim();
    if (!lote || !nome) return true;
    for (const p of paresExclusaoParaCard(lote, nome)) {
      if (pares.has(p)) return false;
    }
    return true;
  });
}

/** Flush síncrono para IDB — chamar após apagar, antes do sync entre janelas. */
export async function flushExclusoesCardsTempoCorte(): Promise<void> {
  const atual = obterExclusoesSincrono();
  cacheExclusoes = atual;
  gravarExclusoesLocal(atual.ids, atual.pares);
  await gravarExclusoesIndexedDB(atual.ids, atual.pares);
}
